import normalFlap from '../../assets/hunt/birds/normal_flap.png';
import fastFlap from '../../assets/hunt/birds/fast_flap.png';
import bonusFlap from '../../assets/hunt/birds/bonus_flap.png';
import hitAfraid from '../../assets/hunt/birds/hit_afraid.png';
import hitFeathers from '../../assets/hunt/fx/hit_feathers.png';
import skyBg from '../../assets/hunt/bg/sky.png';
import crosshairImg from '../../assets/hunt/ui/crosshair.png';

export type BirdType = 'normal' | 'fast' | 'bonus';

/** Durée unique : 60 s, difficulté progressive en fin de partie */
export const HUNT_DURATION_MS = 60000;

export const BIRD_POINTS: Record<BirdType, number> = {
    normal: 10,
    fast: 20,
    bonus: 50,
};

export const COMBO_MILESTONES = [
    { streak: 3, bonus: 5 },
    { streak: 5, bonus: 12 },
    { streak: 10, bonus: 25 },
    { streak: 15, bonus: 45 },
    { streak: 20, bonus: 70 },
    { streak: 25, bonus: 100 },
] as const;

export function getComboMilestone(streak: number): { streak: number; bonus: number } | null {
    return COMBO_MILESTONES.find((m) => m.streak === streak) ?? null;
}

/** Display scale in-game (1 = cell size from sprite sheet; tune in hunt-sprites.config.mjs instead) */
export const HUNT_BIRD_DISPLAY_SCALE: Record<BirdType, number> = {
    normal: 1,
    fast: 1,
    bonus: 1,
};

/** Spawn lanes — used only for occasional weighted spawns; most birds use uniform Y. */
export const HUNT_SPAWN_LANES = [0.10, 0.24, 0.40, 0.56, 0.72] as const;

/** Playable vertical band (fraction of canvas height). */
export const HUNT_EDGE_Y_MIN = 0.05;
export const HUNT_EDGE_Y_MAX = 0.82;

export const HUNT_BEST_LOCAL_KEY = 'birds_hunt_best_local';

/** Classements / soumission score — activer après déploiement du backend hunt.php */
export const HUNT_ONLINE_FEATURES = true;

/** Vertical bob + scale pulse on top of sprite frames — disable to judge raw sheet animation */
export const HUNT_BIRD_MOTION_FX = false;

export interface HuntSpriteSheetDef {
    src: string;
    frameWidth: number;
    frameHeight: number;
    frameCount: number;
    scale: number;
}

export const HUNT_SPRITE_SHEETS = {
    normalFlap: { src: normalFlap, frameWidth: 96, frameHeight: 96, frameCount: 3, scale: HUNT_BIRD_DISPLAY_SCALE.normal },
    fastFlap: { src: fastFlap, frameWidth: 64, frameHeight: 64, frameCount: 3, scale: HUNT_BIRD_DISPLAY_SCALE.fast },
    bonusFlap: { src: bonusFlap, frameWidth: 128, frameHeight: 128, frameCount: 3, scale: HUNT_BIRD_DISPLAY_SCALE.bonus },
    hitAfraid: { src: hitAfraid, frameWidth: 96, frameHeight: 96, frameCount: 1, scale: HUNT_BIRD_DISPLAY_SCALE.normal },
    hitFeathers: { src: hitFeathers, frameWidth: 32, frameHeight: 32, frameCount: 3, scale: 1 },
} as const satisfies Record<string, HuntSpriteSheetDef>;

export const HUNT_BG = {
    sky: skyBg,
} as const;

export const HUNT_UI = {
    crosshair: crosshairImg,
} as const;

/** Per-bird-type sheet lookup used by the engine (dimensions + frame count). */
export const HUNT_ASSETS: Record<BirdType, { scale: number; frameWidth: number; frameHeight: number; frameCount: number }> = {
    normal: HUNT_SPRITE_SHEETS.normalFlap,
    fast: HUNT_SPRITE_SHEETS.fastFlap,
    bonus: HUNT_SPRITE_SHEETS.bonusFlap,
};

export interface SpawnDifficulty {
    minMs: number;
    maxMs: number;
    speedMin: number;
    speedMax: number;
    fastChance: number;
}

/** progress 0 = début calme, 1 = dernières secondes intenses */
export function getSpawnDifficulty(elapsedMs: number): SpawnDifficulty {
    const progress = Math.min(1, elapsedMs / HUNT_DURATION_MS);
    const t = progress * progress;
    return {
        minMs: 2400 - t * 1500,
        maxMs: 3400 - t * 2000,
        speedMin: 1.3 + t * 1.5,
        speedMax: 2.0 + t * 2.5,
        fastChance: 0.12 + t * 0.38,
    };
}

export function getLocalBest(): number {
    try {
        const raw = localStorage.getItem(HUNT_BEST_LOCAL_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (typeof parsed === 'number') {
                return parsed;
            }
            if (parsed && typeof parsed.standard === 'number') {
                return parsed.standard;
            }
        }
    } catch {
        // ignore
    }
    return 0;
}

export function updateLocalBest(score: number): number {
    const current = getLocalBest();
    if (score > current) {
        localStorage.setItem(HUNT_BEST_LOCAL_KEY, JSON.stringify(score));
        return score;
    }
    return current;
}

/** Valeur envoyée au backend quand les scores en ligne seront activés */
export const HUNT_BACKEND_MODE = 'standard' as const;
